import { readManifestState } from "../hooks/useStreamPlayback.js";

// "Can this link be played?" for a stream link the admin typed in (the "Web Page"
// streaming vendor). Unlike a Mux live stream there is no health telemetry behind it and it
// need not be live, so the question is only whether a viewer's player can open it:
//   - an HLS playlist (.m3u8): it must be readable and hold video segments - a live one or
//     a finished (VOD) one both play. A master playlist is followed to its variant.
//   - anything else (.mp4, .webm, ...): the browser itself is asked - a hidden <video> loads
//     the link's metadata. That needs no CORS from the other site and fails exactly when
//     the browser can't play it (bad link, wrong file, unsupported format).
// Resolves { playable, reason }; never rejects.

export const LINK_CHECK_TIMEOUT_MS = 15000;
const MAX_VARIANT_HOPS = 3;

const REASONS = {
  "no-url": "There is no stream link",
  unreachable: "The link can't be reached",
  "invalid-manifest": "It isn't a playable HLS playlist",
  unplayable: "The browser can't play this link",
  timeout: "The link didn't answer in time",
};

export function linkCheckReasonText(reason) {
  return REASONS[reason] || reason || "";
}

export function isHlsLink(url) {
  try {
    return /\.m3u8$/i.test(new URL(url).pathname);
  } catch {
    return false;
  }
}

async function fetchText(url) {
  try {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) return null;
    return await res.text();
  } catch {
    return null;
  }
}

async function checkHlsLink(url) {
  let current = url;
  for (let hop = 0; hop <= MAX_VARIANT_HOPS; hop++) {
    const text = await fetchText(current);
    if (text == null) return { playable: false, reason: "unreachable" };
    const state = readManifestState(text, null);
    if (state.status === "master") {
      current = new URL(state.nextUrl, current).href;
      continue;
    }
    // "sample" = a live playlist with segments, "ended" = a complete (VOD) one.
    if (state.status === "sample" || state.status === "ended") return { playable: true, reason: "playable" };
    return { playable: false, reason: "invalid-manifest" };
  }
  return { playable: false, reason: "invalid-manifest" };
}

function checkMediaLink(url, timeoutMs, createVideo) {
  return new Promise((resolve) => {
    const video = createVideo();
    let timer;
    const finish = (result) => {
      clearTimeout(timer);
      video.removeEventListener("loadedmetadata", onLoaded);
      video.removeEventListener("error", onError);
      // Let go of the media - nothing keeps downloading after the answer.
      video.removeAttribute("src");
      try {
        video.load();
      } catch {
        /* nothing loaded */
      }
      resolve(result);
    };
    const onLoaded = () => finish({ playable: true, reason: "playable" });
    const onError = () => finish({ playable: false, reason: "unplayable" });
    timer = setTimeout(() => finish({ playable: false, reason: "timeout" }), timeoutMs);
    video.muted = true;
    video.preload = "metadata";
    video.addEventListener("loadedmetadata", onLoaded);
    video.addEventListener("error", onError);
    video.src = url;
  });
}

export async function checkLinkPlayable(
  url,
  { timeoutMs = LINK_CHECK_TIMEOUT_MS, createVideo = () => document.createElement("video") } = {},
) {
  if (!url) return { playable: false, reason: "no-url" };
  if (isHlsLink(url)) {
    let timer;
    return Promise.race([
      checkHlsLink(url),
      new Promise((resolve) => {
        timer = setTimeout(() => resolve({ playable: false, reason: "timeout" }), timeoutMs);
      }),
    ]).finally(() => clearTimeout(timer));
  }
  return checkMediaLink(url, timeoutMs, createVideo);
}
